/**
 * Renko Chart Calculator
 *
 * Three modes for brick size calculation:
 * - auto: Calculate brick size to produce ~20 bricks (matches ChartIQ behavior)
 * - fixed: Use user-defined brick size
 * - atr: Use ATR (Average True Range) with configurable period and multiplier
 */

import { isNotDefined, functor } from "../utils";

import { Renko as defaultOptions } from "./defaultOptionsForComputation";

/**
 * Calculate ATR (Average True Range) for the data
 * Uses Simple Moving Average of the last N True Ranges (not Wilder's smoothing)
 * This gives current volatility based on recent data only
 *
 * @param {Array} data - OHLC data array
 * @param {number} period - ATR period (default: 14)
 * @returns {number} - The current ATR value
 */
function calculateATR(data, period = 14) {
  if (!data || data.length < period + 1) {
    return null;
  }

  // Calculate True Range for the last (period + 1) bars only
  // This gives us exactly 'period' true ranges for the SMA
  const startIndex = Math.max(1, data.length - period);
  const trueRanges = [];

  for (let i = startIndex; i < data.length; i++) {
    const current = data[i];
    const previous = data[i - 1];

    const highLow = current.high - current.low;
    const highPrevClose = Math.abs(current.high - previous.close);
    const lowPrevClose = Math.abs(current.low - previous.close);

    const trueRange = Math.max(highLow, highPrevClose, lowPrevClose);
    trueRanges.push(trueRange);
  }

  if (trueRanges.length === 0) {
    return null;
  }

  // Simple Moving Average of True Ranges
  const sum = trueRanges.reduce((acc, tr) => acc + tr, 0);
  const atr = sum / trueRanges.length;

  return atr;
}

export default function () {
  let options = defaultOptions;

  let dateAccessor = (d) => d.date;
  let dateMutator = (d, date) => {
    d.date = date;
  };

  function calculator(rawData) {
    const {
      mode = "auto",
      brickSize: configBrickSize,
      atrPeriod = 14,
      atrMultiplier = 1.0,
    } = options;

    // Always use close price (ChartIQ compatibility)
    const pricingMethod = (d) => ({ high: d.close, low: d.close });

    let brickSize;

    // Safety check for initial data
    if (!rawData || rawData.length === 0) {
      console.warn("Renko: No data to process");
      return [];
    }

    // Determine brick size based on mode
    // Note: Also handles backward compatibility with old configs that only had brickSize
    if (mode === "atr") {
      // ATR MODE: Calculate brick size from ATR
      const atrValue = calculateATR(rawData, atrPeriod);

      if (atrValue && isFinite(atrValue) && atrValue > 0) {
        const calculatedSize = atrValue * atrMultiplier;
        brickSize = functor(calculatedSize);
      } else {
        console.warn(
          "Renko ATR: Could not calculate ATR, falling back to auto mode",
        );
        // Fallback to auto calculation
        brickSize = calculateAutoBrickSize(rawData);
      }
    } else if (configBrickSize && configBrickSize > 0) {
      // FIXED MODE: Use configured value
      // This handles both:
      // - New format: { mode: 'fixed', brickSize: 100 }
      // - Old format: { brickSize: 100 } (backward compatibility)
      brickSize = functor(configBrickSize);
    } else {
      // AUTO MODE: Calculate brick size to produce ~20 bricks
      brickSize = calculateAutoBrickSize(rawData);
    }

    // Helper function for auto brick size calculation
    function calculateAutoBrickSize(data) {
      const sampleSize = Math.min(300, data.length);
      const recentData = data.slice(data.length - sampleSize);

      // Calculate min/max from Close prices
      let minPrice = Infinity;
      let maxPrice = -Infinity;

      recentData.forEach((d) => {
        // Number.isFinite rejects null/undefined/NaN AND +/-Infinity. A plain
        // !isNaN() check lets Infinity through, poisoning priceRange and
        // collapsing the auto brick size to the 0.5 fallback (issue #323).
        const prices = [d.close, d.high, d.low].filter((p) =>
          Number.isFinite(p),
        );
        prices.forEach((p) => {
          if (p < minPrice) minPrice = p;
          if (p > maxPrice) maxPrice = p;
        });
      });

      const priceRange = maxPrice - minPrice;

      // Validate price range
      if (!isFinite(priceRange) || priceRange <= 0) {
        return functor(0.5);
      }

      // Target ~20 bricks visible (matches ChartIQ observed behavior)
      const targetBricks = 20;
      const calculatedSize = priceRange / targetBricks;

      // Final validation
      if (!isFinite(calculatedSize) || calculatedSize <= 0) {
        return functor(0.5);
      }

      return functor(calculatedSize);
    }

    const renkoData = [];

    // Initialize with first bar's open or close
    const firstBar = rawData[0];
    const initialPrice = firstBar.open || firstBar.close || 0;

    let index = 0,
      prevBrickClose = initialPrice,
      prevBrickOpen = initialPrice;
    let brick = {},
      direction = 0;

    rawData.forEach(function (d, idx) {
      if (isNotDefined(brick.from)) {
        brick.high = d.high;
        brick.low = d.low;
        brick.startOfYear = d.startOfYear;
        brick.startOfQuarter = d.startOfQuarter;
        brick.startOfMonth = d.startOfMonth;
        brick.startOfWeek = d.startOfWeek;

        brick.from = idx;
        brick.fromDate = dateAccessor(d);
        dateMutator(brick, dateAccessor(d));
      }
      brick.volume = (brick.volume || 0) + d.volume;

      const prevCloseToHigh = prevBrickClose - pricingMethod(d).high,
        prevCloseToLow = prevBrickClose - pricingMethod(d).low,
        prevOpenToHigh = prevBrickOpen - pricingMethod(d).high,
        prevOpenToLow = prevBrickOpen - pricingMethod(d).low,
        priceMovement = Math.min(
          Math.abs(prevCloseToHigh),
          Math.abs(prevCloseToLow),
          Math.abs(prevOpenToHigh),
          Math.abs(prevOpenToLow),
        );

      brick.high = Math.max(brick.high, d.high);
      brick.low = Math.min(brick.low, d.low);

      if (!brick.startOfYear) {
        brick.startOfYear = d.startOfYear;
        if (brick.startOfYear) {
          dateMutator(brick, dateAccessor(d));
        }
      }

      if (!brick.startOfQuarter) {
        brick.startOfQuarter = d.startOfQuarter;
        if (brick.startOfQuarter && !brick.startOfYear) {
          dateMutator(brick, dateAccessor(d));
        }
      }

      if (!brick.startOfMonth) {
        brick.startOfMonth = d.startOfMonth;
        if (brick.startOfMonth && !brick.startOfQuarter) {
          dateMutator(brick, dateAccessor(d));
        }
      }
      if (!brick.startOfWeek) {
        brick.startOfWeek = d.startOfWeek;
        if (brick.startOfWeek && !brick.startOfMonth) {
          dateMutator(brick, dateAccessor(d));
        }
      }

      if (brickSize(d)) {
        const noOfBricks = Math.floor(priceMovement / brickSize(d));

        brick.open =
          Math.abs(prevCloseToHigh) < Math.abs(prevOpenToHigh) ||
          Math.abs(prevCloseToLow) < Math.abs(prevOpenToLow)
            ? prevBrickClose
            : prevBrickOpen;

        if (noOfBricks >= 1) {
          let j = 0;
          for (j = 0; j < noOfBricks; j++) {
            brick.close =
              brick.open < pricingMethod(d).high
                ? // if brick open is less than current price it means it is green/hollow brick
                  brick.open + brickSize(d)
                : brick.open - brickSize(d);
            direction = brick.close > brick.open ? 1 : -1;
            brick.direction = direction;
            brick.to = idx;
            brick.toDate = dateAccessor(d);
            brick.fullyFormed = true;
            renkoData.push(brick);

            prevBrickClose = brick.close;
            prevBrickOpen = brick.open;

            const newBrick = {
              high: brick.high,
              low: brick.low,
              open: brick.close,
              startOfYear: false,
              startOfMonth: false,
              startOfQuarter: false,
              startOfWeek: false,
            };
            brick = newBrick;
            brick.from = idx;
            brick.fromDate = dateAccessor(d);
            dateMutator(brick, dateAccessor(d));
            brick.volume = (brick.volume || 0) + d.volume;
          }
          index = index + j - 1;
          brick = {};
        } else {
          if (idx === rawData.length - 1) {
            brick.close =
              direction > 0 ? pricingMethod(d).high : pricingMethod(d).low;
            brick.to = idx;
            brick.toDate = dateAccessor(d);
            dateMutator(brick, dateAccessor(d));
            brick.fullyFormed = false;
            renkoData.push(brick);
          }
        }
      }
    });

    return renkoData;
  }
  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  calculator.dateMutator = function (x) {
    if (!arguments.length) return dateMutator;
    dateMutator = x;
    return calculator;
  };
  calculator.dateAccessor = function (x) {
    if (!arguments.length) return dateAccessor;
    dateAccessor = x;
    return calculator;
  };

  return calculator;
}

// Export ATR calculation for use in modal preview
export { calculateATR };
